import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { Box, TableRow, TableCell, Chip, Tooltip, Typography } from '@mui/material'
import { formatYearsExperience } from 'pages/Hospitals/Candidates/candidateDisplayHelpers'
import posthog from 'posthog-js'
import { MATCH_TIERS } from './candidateMatchUtils'

const getMatchTierStyle = (matchTier, theme) => {
  switch (matchTier) {
    case MATCH_TIERS.EXCELLENT:
      return { backgroundColor: theme.palette.success.main, color: 'white' }
    case MATCH_TIERS.STRONG:
      return { backgroundColor: theme.palette.info.main, color: 'white' }
    case MATCH_TIERS.POTENTIAL:
      return { backgroundColor: theme.palette.grey[500], color: 'white' }
    default:
      return { backgroundColor: theme.palette.grey[500], color: 'white' }
  }
}

const buildMatchTooltip = (matchSignals, job) => {
  if (!matchSignals) return ''
  const lines = []
  if (matchSignals.specialties?.length > 0) {
    lines.push(`Specialty: ${matchSignals.specialties.join(', ')}`)
  }
  if (matchSignals.subspecialties?.length > 0) {
    lines.push(`Subspecialty: ${matchSignals.subspecialties.join(', ')}`)
  }
  if (matchSignals.location && job?.state) {
    lines.push(`Location: ${job.state}`)
  }
  return lines.join('\n')
}

const CandidateMatchRow = ({ candidate, job }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const chipStyle = getMatchTierStyle(candidate.matchTier, theme)

  const candidateLocation = [candidate.user?.city, candidate.user?.state].filter(Boolean).join(', ') || '—'
  const experience = formatYearsExperience(candidate.yearsExperience) || '—'
  const specialty = (candidate.specialties || []).map(s => s.name).join(', ') || '—'
  const name = [candidate.user?.firstName, candidate.user?.lastName].filter(Boolean).join(' ')
  const tooltipContent = buildMatchTooltip(candidate.matchSignals, job)

  const handleClick = () => {
    posthog.capture('employer_candidate_match_row_clicked', {
      candidate_id: candidate.id,
      job_id: job?.id,
      match_tier: candidate.matchTier,
    })
    navigate(
      `/hospitals/candidates/${candidate.id}`,
      { state: { from: location.pathname + location.search } },
    )
  }

  return (
    <TableRow
      hover
      onClick={handleClick}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
          {name}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip
          title={
            tooltipContent
              ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>
              : ''
          }
        >
          <Chip
            label={candidate.matchTier}
            size='small'
            sx={{
              backgroundColor: chipStyle.backgroundColor,
              color: chipStyle.color,
              fontWeight: 'bold',
              fontSize: '0.7rem',
            }}
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {candidate.matchSignals?.specialties?.map(specialtyName => (
            <Chip
              key={`specialty-${specialtyName}`}
              label={specialtyName}
              variant='outlined'
              size='small'
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
          {candidate.matchSignals?.subspecialties?.map(subspecialtyName => (
            <Chip
              key={`subspecialty-${subspecialtyName}`}
              label={subspecialtyName}
              variant='outlined'
              size='small'
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
          {candidate.matchSignals?.location && job?.state && (
            <Chip
              label={job.state}
              variant='outlined'
              size='small'
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{candidateLocation}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{experience}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{specialty}</Typography>
      </TableCell>
    </TableRow>
  )
}

export default CandidateMatchRow
